import { BrandedUrl } from "@/api/types";
import { Organization } from "@/modules/common/dtos/index.v2";
import { EnvironmentVariables } from "@/modules/common/types";
import axios, { AxiosRequestConfig } from "axios";
import Cookies from "js-cookie";

interface BrandedUrlFetchOptions<T> extends AxiosRequestConfig {
  brandedUrl: BrandedUrl<T>;
  isEncoded: boolean;
  formData?: boolean;
  includeOrganizationUid?: boolean;
  includeMultipleOrganizations?: boolean;
  includeSingleOrganization?: boolean;
  headers?: {
    apiKey?: string;
    "Content-Type"?: string;
    "Cache-Control"?: string;
  };
}
async function fetchFromBrandedUrl<T>({
  options,
}: {
  options?: BrandedUrlFetchOptions<T>;
}): Promise<T> {
  try {
    if (options && shouldProcessSearchParams(options)) {
      const searchParams = buildSearchParams(options);

      const apiUrl = brandUrl<T>(
        `${options.brandedUrl}?${
          options.isEncoded ? encodeURIComponent(searchParams) : searchParams
        }`
      );

      options.brandedUrl = apiUrl;
      options.params = undefined;
    }

    const url = options?.brandedUrl;

    const usernameString = `CognitoIdentityServiceProvider.${EnvironmentVariables.AWS_CLIENT_ID}.LastAuthUser`;
    const username = localStorage.getItem(usernameString);
    const idTokenKey = `CognitoIdentityServiceProvider.${EnvironmentVariables.AWS_CLIENT_ID}.${username}.idToken`;
    const token = localStorage.getItem(idTokenKey) ?? Cookies.get("accessToken");

    const response = await axios({
      url,
      ...options,
      headers: options?.headers ?? {
        Authorization: `Bearer ${token}`,
        "Content-Type": options?.formData
          ? "multipart/form-data"
          : "application/json",
      },
    });

    return response as T;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (
        error.response?.status === 403 &&
        error.config?.url?.includes("get_pool_lead")
      ) {
        return error.response as T;
      }
      if (error?.response?.status === 413) {
        throw new Error(
          "The uploaded file is too large. Please try a smaller file."
        );
      }

      // Handle network error fallback (likely CloudFront silently dropped it)
      if (error.code === "ERR_NETWORK" && error.message === "Network Error") {
        throw new Error(
          "Upload failed due to network or file size limit. Try uploading a smaller file."
        );
      }

      throw error;
    } else {
      throw new Error("An unexpected error occurred");
    }
  }
}

function brandUrl<T>(url: string): BrandedUrl<any> {
  return url as BrandedUrl<T>;
}

const serializeObjectToQueryString = (obj: object) => {
  return Object.entries(obj)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value
          .map(
            (item) => `${encodeURIComponent(key)}[]=${encodeURIComponent(item)}`
          )
          .join("&");
      }

      if (value === undefined) {
        return undefined;
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(
        value as string | number | boolean
      )}`;
    })
    .filter(Boolean) // Filter out undefined values
    .join("&");
};

function shouldProcessSearchParams<T>(
  options?: BrandedUrlFetchOptions<T>
): boolean {
  return (
    options?.includeOrganizationUid ||
    options?.params ||
    options?.includeMultipleOrganizations ||
    options?.includeSingleOrganization
  );
}

function buildSearchParams<T>(options?: BrandedUrlFetchOptions<T>): string {
  if (!options) return "";

  const selectedOrganizations = getLocalStorageArray("selectedOrganizations")
    .map((org: Organization) => org.uid)
    .join(",");

  const selectedSingleOrganization = getLocalStorageObject<Organization>(
    "selectedSingleOrganization"
  );

  return serializeObjectToQueryString({
    ...options.params,
    ...(options.includeOrganizationUid && {
      organizationUId: getLocalStorageItem("selectedOrganizationUid"),
    }),
    ...(options.includeSingleOrganization && {
      organizationUId:
        selectedSingleOrganization?.uid ??
        getLocalStorageItem("selectedOrganizationUid"),
    }),
    ...(options.includeMultipleOrganizations && {
      organizationUIds:
        selectedOrganizations || getLocalStorageItem("selectedOrganizationUid"),
    }),
  });
}

function getLocalStorageItem(key: string): string | null {
  return localStorage.getItem(key);
}

function getLocalStorageArray(key: string): any[] {
  return JSON.parse(localStorage.getItem(key) || "[]");
}

function getLocalStorageObject<T>(key: string): T | null {
  return JSON.parse(localStorage.getItem(key) || "");
}
export {
  fetchFromBrandedUrl,
  brandUrl,
  serializeObjectToQueryString,
  buildSearchParams,
  shouldProcessSearchParams,
};
